import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Dropdown } from 'react-bootstrap';
import LoadingIconButton from 'y4/components/common/LoadingIconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getDD, getListByDD } from 'y4/Y4Api';
import y, {
  getQueries,
  formatByNamedArgs,
  isEmptyThenTo,
  setQueriesOther
} from 'y4/Utils';
import { run as runCommand } from '../Executer';
import FormInputGroup from './FormInputGroup';
import { useNavigate, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

/** Load all data from back end for DataPanel
 * @param {*} panel Data panel of Y4
 * @param {*} withDd false: Without Data defination
 * @param {*} queries
 * @returns
 */
async function loadData(form, panel, withDd = true, queries = {}) {
  const dataCode = panel.dataCode;

  //Get data definition
  let rt_dd = null;
  if (withDd) {
    rt_dd = await getDD(dataCode, queries);
    if (rt_dd.isNotOk()) return { rt: rt_dd };
  }

  //Set queries other field
  setQueriesOther(form, queries);

  //Get all list and statistics by data definitioin
  const rt_data = await getListByDD(dataCode, {
    count: 1,
    statistics: false,
    ...queries
  });
  if (rt_data.isNotOk()) return { rt: rt_data };

  //Return async data
  return {
    rt: rt_data, //last rt
    fields: rt_dd && rt_dd.data.fields,
    rows: rt_data.data.list //rows
  };
}

/**
 * @param {*} panel
 * @returns
 */
const FormPanel = ({ form, panel }) => {
  const { t } = useTranslation('data');
  const load_title = y.fne(_.get(panel, 'option.load.title'), t('Loading....'));
  const load_message = y.fne(
    _.get(panel, 'option.load.message'),
    t('Data is still loading, please wait')
  );

  const location = useLocation();
  var queries = getQueries(location.search);
  //Tool using to navigate
  const navigate = useNavigate();

  //Data to show by state
  const [data, setData] = useState({
    error: 1,
    errorTitle: load_title,
    errorDescription: load_message
  });

  function setDataAndDispatch(d) {
    setData(d);
    form.eventTarget.dispatchEvent(
      new CustomEvent('form-event', {
        detail: {
          event: 'afterPanelLoaded',
          panel,
          form,
          data: d
        }
      })
    );
  }

  //Load data after UI shown
  useEffect(() => {
    //Load data from back end
    loadData(form, panel, true, queries).then(({ rt, fields, rows }) => {
      if (rt.isOk()) {
        if (_.isEmpty(rows)) {
          setData({
            error: 1,
            errorTitle: 'No data',
            errorDescription: 'No data to display'
          });
        } else {
          setDataAndDispatch({ row: rows[0], fields });
        }
      } else {
        setData({
          error: 1,
          errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
          errorDescription: rt.message
        });
      }
    });
  }, []);

  const checkRequired = () => {
    const required_fields = data.fields.filter(field => {
      return !field.hidden && field.required;
    });

    const error_fields = new Set([]);
    for (const rf of required_fields) {
      const fv = data.row[rf.name + 'Ui'];
      if (_.isEmpty(fv)) {
        error_fields.add(rf.title);
      }
    }

    if (error_fields.size > 0) {
      const tns = _.join(Array.from(error_fields), ',');
      const template = t('Please fill out this fields:{names}');
      y.toastError(formatByNamedArgs(template, { names: tns }));
      return false;
    } else {
      return true;
    }
  };

  const buttonClick = button => {
    if (button.command == 'SaveByDataDefinition') {
      if (!checkRequired()) return;
    }
    return runCommand({
      form,
      panel,
      command: button.command,
      params: button.params,
      javascript: button.javascript,
      dataCode: panel.dataCode,
      successMessage: button.successMessage,
      successCommand: button.successCommand,
      navigate: navigate,
      rows: [data.row],
      queries: queries
    });
  };

  const et = new EventTarget();

  const itemChanged = e => {
    const new_data = { ...data };
    let field;
    let value;
    if (e.target) {
      field = e.target.name;
      if ('checkbox' == e.target.type) {
        value = e.target.checked;
      } else {
        value = e.target.value;
      }
    } else {
      field = e.name;
      value = e.value;
    }
    new_data.row[field] = value;
    if (_.has(e, 'display')) {
      new_data.row[field + 'Ui'] = _.isString(e.display)
        ? e.display.trim()
        : e.display;
    }
    setData(new_data);
    //Riase event after changed set other action
    et.dispatchEvent(
      new CustomEvent('itemChanged', {
        detail: { data: new_data, field, value }
      })
    );
  };

  const footerButtons =
    panel.buttons &&
    panel.buttons.filter(item => {
      return item.type == 'footer';
    });

  panel.currentRows = [data.row];

  return (
    data &&
    (data.error ? (
      <>
        <h5 className="mb-0 mt-1">{data.errorTitle}</h5>
        <div className="pt-3">{data.errorDescription}</div>
      </>
    ) : (
      <>
        <Row>
          {data.fields
            .filter(field => !field.hidden)
            .map((field, index) => {
              return (
                <FormInputGroup
                  key={index}
                  panel={panel}
                  field={field}
                  data={data}
                  onChange={itemChanged}
                  eventTarget={et}
                />
              );
            })}
        </Row>
        {_.isEmpty(footerButtons) ? null : (
          <>
            <hr />
            <div className="d-flex align-items-center justify-content-between justify-content-lg-end">
              {footerButtons.map((button, index) => {
                if (button.divider) {
                  return (
                    <div
                      key={index}
                      className="bg-300 ms-2 d-none d-lg-block"
                      style={{ width: '1px', height: '29px' }}
                    ></div>
                  );
                } else if (button.expander) {
                  return (
                    <div
                      key={index}
                      className={isEmptyThenTo(button.className, 'flex-grow-1')}
                    ></div>
                  );
                } else if (button.buttons) {
                  return (
                    <Dropdown
                      key={index}
                      align="end"
                      className="btn-reveal-trigger d-inline-block ms-2"
                    >
                      <Dropdown.Toggle variant="primary">
                        <FontAwesomeIcon icon={button.icon} className="fs--2" />
                        {button.text && (
                          <span className="d-none d-sm-inline-block d-xl-none d-xxl-inline-block ms-1">
                            {button.text}
                          </span>
                        )}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="border py-0">
                        <div className="py-2">
                          {button.buttons.map((sub_button, sub_index) => {
                            if (sub_button.divider) {
                              return <Dropdown.Divider key={sub_index} />;
                            } else {
                              return (
                                <Dropdown.Item
                                  key={sub_index}
                                  as="button"
                                  type="button"
                                  className={sub_button.className}
                                  onClick={() => buttonClick(sub_button)}
                                >
                                  {sub_button.text}
                                </Dropdown.Item>
                              );
                            }
                          })}
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  );
                } else {
                  return (
                    <LoadingIconButton
                      type="submit"
                      key={index}
                      variant="primary"
                      loadingText={button.waitingText}
                      loadingIcon={button.waitingIcon}
                      icon={button.icon}
                      onClick={() => buttonClick(button)}
                      className={isEmptyThenTo(button.className, 'ms-2')}
                      transform="shrink-3"
                      iconAlign="middle"
                    >
                      <span
                        className={isEmptyThenTo(
                          button.textClassName,
                          'd-inline-block ms-1'
                        )}
                      >
                        {button.text}
                      </span>
                    </LoadingIconButton>
                  );
                }
              })}
            </div>
          </>
        )}
      </>
    ))
  );
};

FormPanel.propTypes = {
  form: PropTypes.object,
  panel: PropTypes.object
};

export default FormPanel;
